/**
 * =============================================================================
 * SystemTemplatesTabs — abas da página Templates do Sistema
 *
 * Espelha o visual de `ListsHubTabs` (Central de Importação de Listas):
 * fundo suave, ícone à esquerda do nome e faixa colorida na aba ativa.
 *
 * @version 1.0.0
 * RELEASE: 2026-09-08 v1.0.0 - Versão inicial (padronização com Importação de Listas).
 * =============================================================================
 */
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { Layers, History } from 'lucide-react';

export type SystemTemplatesTabKey = 'templates' | 'execucoes';

export interface SystemTemplatesTabDef {
  key: SystemTemplatesTabKey;
  label: string;
  /** Cor do indicador (token semântico). */
  color: string;
}

interface SystemTemplatesTabsProps {
  tabs: SystemTemplatesTabDef[];
  value: SystemTemplatesTabKey;
  onChange: (key: SystemTemplatesTabKey) => void;
}

const ICONS: Record<SystemTemplatesTabKey, React.ComponentType<{ className?: string }>> = {
  templates: Layers,
  execucoes: History,
};

export function SystemTemplatesTabs({ tabs, value, onChange }: SystemTemplatesTabsProps) {
  if (tabs.length === 0) return null;

  return (
    <div className="flex items-center gap-2 w-full overflow-x-auto scrollbar-hide pb-1 -mb-1">
      <Tabs
        value={value}
        onValueChange={(v) => onChange(v as SystemTemplatesTabKey)}
        className="w-full"
      >
        <TabsList className="h-9 bg-muted/50">
          {tabs.map((tab) => {
            const Icon = ICONS[tab.key];
            return (
              <TabsTrigger
                key={tab.key}
                value={tab.key}
                className={cn(
                  'px-4 text-sm whitespace-nowrap',
                  'data-[state=active]:bg-background',
                  'transition-all duration-200',
                )}
                style={{
                  borderBottomColor: value === tab.key ? tab.color : 'transparent',
                  borderBottomWidth: 2,
                }}
              >
                <Icon className="w-4 h-4 mr-2" />
                {tab.label}
              </TabsTrigger>
            );
          })}
        </TabsList>
      </Tabs>
    </div>
  );
}

export default SystemTemplatesTabs;
